import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authApi, setAuthToken, getAuthToken, clearAuth, setAuthUser, getAuthUser } from './api';

export interface AuthUser {
  id: number;
  username: string;
  email: string;
  nickname: string;
  avatar: string;
  phone: string;
  bio: string;
  mbti_type?: string;
  mbti_updated_at?: string | null;
  is_active: boolean;
  is_superuser: boolean;
  is_staff: boolean;
  roles: { id: number; code: string; name: string; description: string }[];
  permissions: string[];
}

interface AuthContextType {
  user: AuthUser | null;
  loading: boolean;
  isAuthenticated: boolean;
  login: (username: string, password: string) => Promise<void>;
  register: (username: string, password: string, nickname?: string) => Promise<void>;
  loginWithTokenAndUser: (token: string, userData: any) => void;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
  hasPermission: (perm: string) => boolean;
  hasRole: (role: string) => boolean;
  isAdmin: boolean;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  loading: true,
  isAuthenticated: false,
  login: async () => {},
  register: async () => {},
  loginWithTokenAndUser: () => {},
  logout: async () => {},
  refreshUser: async () => {},
  hasPermission: () => false,
  hasRole: () => false,
  isAdmin: false,
});

export const useAuth = () => useContext(AuthContext);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<AuthUser | null>(getAuthUser());
  const [loading, setLoading] = useState(!!getAuthToken());

  const refreshUser = useCallback(async () => {
    const token = getAuthToken();
    if (!token) {
      setUser(null);
      setLoading(false);
      return;
    }
    try {
      const res = await authApi.getMe();
      setUser(res.data);
      setAuthUser(res.data);
    } catch {
      clearAuth();
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (getAuthToken()) {
      refreshUser();
    } else {
      setLoading(false);
    }
  }, [refreshUser]);

  const login = async (username: string, password: string) => {
    const res = await authApi.login({ username, password, device_name: navigator.userAgent.slice(0, 50) });
    setAuthToken(res.data.token);
    setUser(res.data.user);
    setAuthUser(res.data.user);
  };

  const register = async (username: string, password: string, nickname?: string) => {
    const res = await authApi.register({ username, password, nickname });
    setAuthToken(res.data.token);
    setUser(res.data.user);
    setAuthUser(res.data.user);
  };

  // 直接用 token 和 user 数据完成登录（扫码登录轮询成功后使用）
  const loginWithTokenAndUser = (token: string, userData: any) => {
    setAuthToken(token);
    setUser(userData);
    setAuthUser(userData);
  };

  const logout = async () => {
    try {
      const { unregisterCurrentDevice } = await import('./pushNotifications');
      await unregisterCurrentDevice();
    } catch { /* ignore */ }
    try {
      await authApi.logout();
    } catch { /* ignore */ }
    clearAuth();
    setUser(null);
  };

  const hasPermission = (perm: string) => {
    if (!user) return false;
    if (user.is_superuser) return true;
    return user.permissions?.includes(perm) ?? false;
  };

  const hasRole = (role: string) => {
    if (!user) return false;
    if (user.is_superuser) return true;
    return user.roles?.some(r => r.code === role) ?? false;
  };

  const isAdmin = user?.is_superuser || hasRole('admin');

  return (
    <AuthContext.Provider value={{
      user,
      loading,
      isAuthenticated: !!user,
      login,
      register,
      loginWithTokenAndUser,
      logout,
      refreshUser,
      hasPermission,
      hasRole,
      isAdmin,
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
